/*
  <color-expansion>

  The colour row, and the one control that opens it out.

  The row is the theme's Swiper, untouched. When it holds more than fits across
  the column, a "show all" appears above it; opening turns the row into a
  wrapping grid showing everything at once, and on a product whose other
  colours are separate products it reveals those too.

  Opening moves the tiles rather than rebuilding them. They are radio inputs
  that own the variant selection, so a second copy rendered into a grid would
  share their name, fight over `:checked`, and leave the selected border on
  whichever copy the browser kept. Moving the same nodes keeps one set, and the
  selection with it. Nothing in the theme scopes behaviour to the carousel:
  tile clicks are delegated from the product element, so the tiles work the
  same in either container.

  It goes both ways. The row is hidden rather than taken apart while the grid is
  open, and closing puts the same tiles back in front of the card and tells
  Swiper to measure again, so the carousel that comes back is the one that left.

  Leaving through a tile in the open grid hands the open state to the product
  being opened, once. Arriving any other way starts closed.

  Markup contract:

    <color-expansion>
      <button data-colors-showall hidden>       shown when the row overflows, and
                                                whenever the grid is open
        <element data-colors-showall-text>      swapped between the two labels
      <element data-colors-row>                 the swiper-init element
        <element class="swiper-wrapper">
          <element class="swiper-slide">        one per tile
            <button data-colors-more>           optional, the card at the end
      <element data-colors-grid hidden>         where the tiles move to
      <element data-colors-panel hidden>        optional, other colours below
*/
(function () {
  'use strict';

  /*
    Opening is handed to the next product, once.

    Choosing one of the other colours from inside the open grid is choosing
    another product, and the theme swaps the product in place, so the row would
    come back built from the new product's markup and closed again. Someone
    working through a range would have to reopen it on every strap they tried.

    So the state is not a preference, it is a hand-off: written only when a
    shopper leaves through a tile in the open grid, and read once on the way
    in. Arriving at a product any other way, from the home page, a collection,
    a link, finds no hand-off waiting and gets the row closed, which is the
    right default for someone who has not asked to see everything.

    sessionStorage, so it cannot outlive the tab, and every access is guarded
    because private browsing throws rather than returning null. The key is
    versioned: an older tab may still hold the previous sticky flag under the
    old name, and that value means something different.
  */
  var CARRY_KEY = 'wb:colors-carry';

  function carry() {
    try {
      window.sessionStorage.setItem(CARRY_KEY, '1');
    } catch (err) {
      /* the row still opens, it just will not carry to the next product */
    }
  }

  // Read and clear in one go: a hand-off is spent by being used, so a later
  // arrival from anywhere else starts closed.
  function takeCarry() {
    try {
      if (window.sessionStorage.getItem(CARRY_KEY) !== '1') return false;
      window.sessionStorage.removeItem(CARRY_KEY);
      return true;
    } catch (err) {
      return false;
    }
  }

  /*
    Cancellable, because the element can leave the DOM inside the wait.

    Choosing a colour swaps the product in place, so an element that saw a
    resize a moment earlier is torn down while its timer is still pending.
    Dropping the listener does not reach a call that is already scheduled, and
    that call arrives holding a `this` whose row is gone.
  */
  function debounce(fn, wait) {
    var timer = null;

    function schedule() {
      window.clearTimeout(timer);
      timer = window.setTimeout(fn, wait);
    }

    schedule.cancel = function () {
      window.clearTimeout(timer);
      timer = null;
    };

    return schedule;
  }

  class ColorExpansion extends HTMLElement {
    constructor() {
      super();
      this._expanded = false;
      this._onExpand = this.expand.bind(this);
      this._onToggle = this.toggle.bind(this);
      this._onLeave = this.onLeave.bind(this);
      this._onResize = null;
      this._frame = null;
      // Set here as well as on connect, so that every method has one answer to
      // "am I set up", whether it runs before the first connect or after a
      // teardown.
      this.row = null;
      this.grid = null;
      this.track = null;
    }

    connectedCallback() {
      this.row = this.querySelector('[data-colors-row]');
      this.grid = this.querySelector('[data-colors-grid]');
      if (!this.row || !this.grid) return;

      this.track = this.row.querySelector('.swiper-wrapper');
      this.button = this.querySelector('[data-colors-showall]');
      this.card = this.querySelector('[data-colors-more]');
      this.panel = this.querySelector('[data-colors-panel]');
      // The card sits inside a slide rather than being one, so that Swiper's
      // a11y labels land on the wrapper instead of replacing the button's name.
      this.cardSlide = this.card ? this.card.closest('.swiper-slide') : null;

      if (this.button) this.button.addEventListener('click', this._onToggle);
      if (this.card) this.card.addEventListener('click', this._onExpand);
      this.addEventListener('click', this._onLeave);

      this._onResize = debounce(this.measure.bind(this), 150);
      window.addEventListener('resize', this._onResize, { passive: true });

      /*
        One pass after layout is enough. A tile is a fixed 6.25rem with a 1:1
        image box, so where the last one lands does not change when its
        photograph arrives, and waiting for images would never resolve on a
        product whose sibling tiles sit in a display:none panel and are
        therefore never fetched.
      */
      this._frame = window.requestAnimationFrame(this.settle.bind(this));
    }

    settle() {
      this._frame = null;
      // A product swapped inside that one frame leaves this pass with nothing
      // to measure.
      if (!this.row) return;
      this.measure();
      this.restore();
    }

    /*
      Reopen a row the shopper already opened earlier in this tab.

      Only where there is something to open: a row that fits its column and has
      no other colours below it would otherwise be rebuilt as a grid for no
      visible reason, which is a different layout for the same content.

      No focus is moved here. Nobody pressed anything, and taking focus on load
      would drag a keyboard user, and a screen reader, into the middle of the
      page every time they opened a product.
    */
    restore() {
      if (this._expanded || !takeCarry()) return;
      var canOpen = (this.button && !this.button.hidden) || this.panel;
      if (!canOpen) return;
      this.open(false);
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this._onToggle);
      if (this.card) this.card.removeEventListener('click', this._onExpand);
      this.removeEventListener('click', this._onLeave);
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        // The listener is gone, a call already waiting on it is not.
        this._onResize.cancel();
        this._onResize = null;
      }
      if (this._frame !== null) {
        window.cancelAnimationFrame(this._frame);
        this._frame = null;
      }
      this.row = null;
      this.grid = null;
      this.track = null;
    }

    /*
      Whether a whole card is off the end of the row.

      Not whether the row overflows at all. A row that runs a few pixels past
      its edge still shows everything it has, and offering to open that out is
      an invitation to nothing. The question is whether a card is sitting
      entirely out of sight, which is what a shopper would want a way to reach.

      Measured rather than counted against a tiles-per-breakpoint table. The
      column is one width on a phone and another on a desktop, and the answer
      only depends on how many cards there are against how much room, so asking
      the laid-out row is both simpler and correct at widths nobody listed. It
      works out at three across a phone and five or six across a desktop here,
      which is where a table would have landed.

      Read while the row is at rest, which it is until someone swipes it.
    */
    measure() {
      if (this._expanded || !this.row || !this.button || !this.track) return;

      var slides = this.track.querySelectorAll('.swiper-slide');
      if (!slides.length) return;

      var frame = this.row.getBoundingClientRect();
      if (!frame.width) return;

      var hidden = false;
      for (var i = 0; i < slides.length; i += 1) {
        // Starts at or past the edge, so none of it is on screen.
        if (slides[i].getBoundingClientRect().left >= frame.right - 1) {
          hidden = true;
          break;
        }
      }

      this.button.hidden = !hidden;
    }

    expand(event) {
      event.preventDefault();
      this.open(true);
    }

    /*
      Leaving through one of the other colours, while the grid is open.

      Only then. A tile chosen from the closed row is an ordinary click on an
      ordinary link, and the product it opens should look the way it would to
      anyone else arriving at it. Nothing is prevented here: the theme's own
      handler takes the click and swaps the product, and this only notes which
      way the shopper left.
    */
    onLeave(event) {
      if (!this._expanded) return;
      if (!event.target || !event.target.closest) return;
      if (!event.target.closest('.connected-products__tile')) return;
      carry();
    }

    toggle(event) {
      event.preventDefault();
      if (this._expanded) this.close();
      else this.open(true);
    }

    open(moveFocus) {
      if (this._expanded) return;
      this._expanded = true;

      /*
        Focus moves before anything is taken out of the row, because the card
        is about to be hidden and a keyboard user standing on it would
        otherwise be dropped back at the top of the page. Only when someone
        pressed something: a row opened by a hand-off moves nothing.
      */
      this.grid.hidden = false;
      if (moveFocus) {
        this.grid.setAttribute('tabindex', '-1');
        try {
          this.grid.focus({ preventScroll: true });
        } catch (err) {
          this.grid.focus();
        }
      }

      // Hidden rather than removed. The row has to be able to come back.
      if (this.cardSlide) this.cardSlide.hidden = true;

      this.eachTile(this.track, function (slide) {
        slide.classList.remove('swiper-slide');
        // Swiper writes spaceBetween as an inline margin on each slide, which
        // would sit on top of the grid's own gap.
        slide.style.marginRight = '';
        this.grid.appendChild(slide);
      });

      this.row.hidden = true;
      if (this.panel) this.panel.hidden = false;
      this.setButton(true);
    }

    close() {
      if (!this._expanded) return;
      this._expanded = false;

      /*
        Back in front of the card, so the card stays last in the row where it
        was rendered. Appending would leave it stranded among the tiles.
      */
      var anchor = this.cardSlide;
      this.eachTile(this.grid, function (tile) {
        tile.classList.add('swiper-slide');
        if (anchor && anchor.parentNode === this.track) this.track.insertBefore(tile, anchor);
        else this.track.appendChild(tile);
      });

      if (this.cardSlide) this.cardSlide.hidden = false;
      this.grid.hidden = true;
      if (this.panel) this.panel.hidden = true;
      this.row.hidden = false;

      /*
        Swiper measured a row that has changed under it twice over, so it is
        told to look again. The library instance is a level below the theme's
        wrapper: RevolutionSwiper holds it as swiperElement and exposes no
        update of its own.
      */
      var slider = this.row.swiper && this.row.swiper.swiperElement;
      if (slider && typeof slider.update === 'function') slider.update();

      this.setButton(false);
      this.measure();
    }

    // Tiles only. The card's slide is skipped wherever it happens to be.
    eachTile(container, fn) {
      var self = this;
      Array.prototype.slice.call(container.children).forEach(function (el) {
        if (self.cardSlide && el === self.cardSlide) return;
        fn.call(self, el);
      });
    }

    setButton(expanded) {
      if (!this.button) return;
      // Always reachable once open, even on a row short enough that the button
      // was never needed to open it. The card can open a row that fits.
      if (expanded) this.button.hidden = false;
      this.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

      var label = expanded ? this.button.dataset.labelLess : this.button.dataset.labelMore;
      if (!label) return;
      var text = this.button.querySelector('[data-colors-showall-text]');
      if (text) text.textContent = label;
    }
  }

  if (!customElements.get('color-expansion')) {
    customElements.define('color-expansion', ColorExpansion);
  }
})();
